import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import and from 'ember-truth-helpers/helpers/and';
import not from 'ember-truth-helpers/helpers/not';
import BvCollapsible from 'volta/components/bv-collapsible';
import BvIcon from 'volta/components/bv-icon';
import BvSpinner from 'volta/components/bv-spinner';
import classNames from 'volta/helpers/class-names';
import { guid } from 'volta/helpers/guid';
import { argOrDefault } from 'volta/macros/arg-or-default';

import type Owner from '@ember/owner';
import type { ContentValue } from '@glint/template';

type AccordionPaddingSize = 'none' | 'sm' | 'md' | 'lg';

export interface BvAccordionSignature {
  Args: {
    id: string;
    /**
     * Class that will apply to the trigger for the accordion.
     */
    buttonClassName?: string;
    /**
     * Class that will apply to the trigger content for the accordion.
     */
    buttonContentClassName?: string;
    /**
     * The content of the clickable trigger
     */
    buttonContent?: Component;
    /**
     * Will appear right aligned against the button. Useful for separate actions like deletions.
     */
    extraAction?: ContentValue;
    /**
     * The accordion will start in the open state.
     */
    initialIsOpen?: boolean;
    /**
     * Optional callback method called on open and close with a single `isOpen` parameter
     */
    onToggle?: (isOpen: boolean) => void;
    /**
     * The padding around the exposed accordion content.
     */
    paddingSize?: AccordionPaddingSize;
    /**
     * Placement of the arrow indicator, or 'none' to hide it.
     */
    arrowDisplay?: 'left' | 'right' | 'none';
    /**
     * Control the opening of accordion via prop
     */
    forceState?: 'closed' | 'open';
    /**
     * Change `extraAction` and children into a loading spinner
     */
    isLoading?: boolean;
    /**
     * Choose whether the loading message replaces the content. Customize the message by passing a node
     */
    isLoadingMessage?: string;
    triggerClassName?: string;
    childClassName?: string;
    overflowHidden?: boolean;
  };
  Blocks: {
    content: [];
    trigger: [];
  };
  Element: HTMLDivElement;
}

export default class BvAccordionComponent extends Component<BvAccordionSignature> {
  // Defaults
  @argOrDefault(false) isLoading!: boolean;
  @argOrDefault(false) initialIsOpen!: boolean;
  @argOrDefault('none') paddingSize!: AccordionPaddingSize;
  @argOrDefault('left') arrowDisplay!: BvAccordionSignature['Args']['arrowDisplay'];

  @tracked _opened;

  buttonId: string = guid();

  constructor(owner: Owner, args: BvAccordionSignature['Args']) {
    super(owner, args);

    this._opened = this.args.forceState
      ? this.args.forceState === 'open'
      : (this.args.initialIsOpen ?? false);
  }

  get isOpen(): boolean {
    return this.args.forceState ? this.args.forceState === 'open' : this._opened;
  }

  get hasIconButton(): boolean | undefined {
    return isPresent(this.args.extraAction) && this.arrowDisplay === 'right';
  }

  get hasArrowDisplay(): boolean {
    return this.arrowDisplay !== 'none';
  }

  get buttonReverse(): boolean {
    return !this.args.extraAction && this.arrowDisplay === 'right';
  }

  get hasLoadingMessage(): boolean {
    return isPresent(this.args.isLoadingMessage);
  }

  @action
  onToggle(): void {
    const _onToggle = this.args.onToggle;
    if (this.args.forceState && _onToggle) {
      _onToggle(this.args.forceState !== 'open');
    } else {
      this._opened = !this._opened;
      if (_onToggle) {
        _onToggle(this._opened);
      }
    }
  }

  <template>
    <div
      class={{classNames
        'bv-accordion'
        (if this.isOpen 'bv-accordion--is-open')
        (if this.paddingSize (concat 'bv-accordion--padding-size-' this.paddingSize))
      }}
      ...attributes
    >
      <div class={{classNames 'bv-accordion__trigger-wrapper' @triggerClassName}}>
        <button
          class={{classNames
            'bv-accordion__button'
            (if this.buttonReverse 'bv-accordion__button-reverse')
            @buttonClassName
          }}
          id={{this.buttonId}}
          data-test-accordion={{@id}}
          aria-controls={{@id}}
          aria-expanded={{this.isOpen}}
          type='button'
          {{on 'click' this.onToggle}}
        >
          {{#if this.hasArrowDisplay}}
            <span
              class={{classNames
                'bv-accordion__icon-wrapper'
                (if this.hasIconButton 'bv-accordion__icon-button')
              }}
            >
              <BvIcon
                class={{classNames
                  'bv-accordion__icon'
                  (if this.isOpen 'bv-accordion__icon-is-open')
                }}
                @glyph='chevron-forward'
                @size='md'
              />
            </span>
          {{/if}}
          <span class={{concat 'u-flex-wrap-fix ' @buttonContentClassName}}>
            {{yield to='trigger'}}
          </span>
        </button>
        {{#if (and (isPresent @extraAction) (not this.isLoading))}}
          <div class='bv-accordion__optional-action'>
            {{@extraAction}}
          </div>
        {{else if this.isLoading}}
          <div class='bv-accordion__optional-action'>
            <BvSpinner @size='sm' />
          </div>
        {{/if}}
        {{#if this.hasIconButton}}
          <button
            aria-controls={{@id}}
            aria-expanded={{this.isOpen}}
            aria-labelledby={{this.buttonId}}
            tabIndex={{-1}}
            class={{classNames
              'bv-accordion__icon-wrapper'
              (if this.hasIconButton 'bv-accordion__icon-button')
            }}
            type='button'
            {{on 'click' this.onToggle}}
          >
            <BvIcon
              class={{classNames
                'bv-accordion__icon'
                (if this.isOpen 'bv-accordion__icon-is-open')
              }}
              @glyph='chevron-forward'
              @size='md'
            />
          </button>
        {{/if}}
      </div>
      <BvCollapsible
        class='bv-accordion__child-wrapper'
        @id={{@id}}
        @open={{this.isOpen}}
        @overflowHidden={{@overflowHidden}}
      >
        <div
          class={{classNames
            'bv-accordion'
            (if this.isLoading ' bv-accordion__children-is-loading')
            @childClassName
            (if this.paddingSize (concat 'bv-accordion--padding-' this.paddingSize))
          }}
        >
          {{#if (and this.isLoading @isLoadingMessage)}}
            <BvSpinner @size='sm' class='bv-accordion__spinner' />
            <span class='bv-accordion__loading-message'>
              {{#if this.hasLoadingMessage}}
                {{@isLoadingMessage}}
              {{/if}}
            </span>
          {{else}}
            {{yield to='content'}}
          {{/if}}
        </div>
      </BvCollapsible>
    </div>
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvAccordion: typeof BvAccordionComponent;
    'bv-accordion': typeof BvAccordionComponent;
  }
}
